import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded'
import { firebaseAuth } from '../../services/firebase'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useWorkspace } from '../../context/useWorkspace'
import { ToastContext } from '../../context/toastContextCore'
import { requestWorkspaceSwitch } from '../../services/workspaceSwitchService'
import { safeWorkspaceLanding } from '../../services/workspaceSwitch/switchLanding'
import {
  claimPendingInvite,
  declinePendingInvite,
  listIncomingInvitesForEmail,
  listIncomingInvitesForPhone,
} from '../../services/inviteService'
import type { PendingInvite, PendingInviteStatus } from '../../types/invites'
import { useNls } from '../../nls/nlsProvider'
import { formatRoleLabel } from '../../utils/roleLabels'
import { displayNameOrMaskedPhone } from '../../utils/phoneNumber'
import type { NlsKey } from '../../nls/strings_nls'
import { EmptyHubState, RailChipTabs } from '../Common'
import { Button } from '../Common/Button'
import { Loader } from '../Common/Loader'
import { JoinWithCodeDialog } from '../JoinCodes/JoinWithCodeDialog'
import type { ClaimedJoinCodeInfo } from '../JoinCodes/JoinWithCodeForm'
import './IncomingInvitesPage.css'

type IncomingInvitesPageProps = {
  roleHint: 'HELPER' | 'MEMBER'
}

const STATUS_LABEL: Record<PendingInviteStatus, string> = {
  PENDING: 'Pending',
  CLAIMED: 'Accepted',
  DECLINED: 'Rejected',
  REVOKED: 'Revoked',
  EXPIRED: 'Expired',
}

const toMillis = (invite: PendingInvite): number => invite.createdAt?.toMillis?.() ?? 0

const inviteSubtitle = (invite: PendingInvite, t: (key: NlsKey) => string): string => {
  const method = invite.type === 'PHONE'
    ? displayNameOrMaskedPhone(null, invite.recipient)
    : invite.type === 'EMAIL'
      ? invite.recipient
      : 'Shared link'
  return `${formatRoleLabel(t, invite.role)} invite · ${method}`
}

export const IncomingInvitesPage = (_props: IncomingInvitesPageProps) => {
  const { t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const { workspaces, refreshWorkspaces } = useWorkspace()
  const toastContext = useContext(ToastContext)
  const showToast = toastContext?.showToast
  const [invites, setInvites] = useState<PendingInvite[]>([])
  const [filter, setFilter] = useState<'ALL' | PendingInviteStatus>('ALL')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [joinWithCodeOpen, setJoinWithCodeOpen] = useState(false)

  const claimingUser = useMemo(() => ({
    uid: currentUser?.uid ?? '',
    email: currentUser?.email ?? null,
    phoneE164: currentUser?.phoneNumber ?? null,
    displayName: displayName || currentUser?.email || currentUser?.phoneNumber || null,
  }), [currentUser, displayName])

  const load = useCallback(async () => {
    if (!currentUser) {
      setInvites([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [emailInvites, phoneInvites] = await Promise.all([
        currentUser.email ? listIncomingInvitesForEmail(currentUser.email) : Promise.resolve([]),
        currentUser.phoneNumber ? listIncomingInvitesForPhone(currentUser.phoneNumber) : Promise.resolve([]),
      ])
      const byId = new Map<string, PendingInvite>()
      for (const invite of [...emailInvites, ...phoneInvites]) byId.set(invite.id, invite)
      setInvites(Array.from(byId.values()).sort((a, b) => toMillis(b) - toMillis(a)))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load incoming invites.')
    } finally {
      setLoading(false)
    }
  }, [currentUser])

  useEffect(() => {
    void load()
  }, [load])

  const visibleInvites = useMemo(
    () => invites.filter((invite) => filter === 'ALL' || invite.status === filter),
    [filter, invites],
  )
  const counts = useMemo(() => invites.reduce<Record<PendingInviteStatus, number>>((next, invite) => {
    next[invite.status] += 1
    return next
  }, { PENDING: 0, CLAIMED: 0, DECLINED: 0, REVOKED: 0, EXPIRED: 0 }), [invites])

  const handleJoinedWorkspace = useCallback(async (joinedWsId: string, joinedWsName?: string) => {
    await firebaseAuth.currentUser?.getIdToken(true)
    const refreshed = await refreshWorkspaces(undefined, undefined, { quiet: true })
    const wsList = (Array.isArray(refreshed) ? refreshed : workspaces) ?? []
    const joinedWs = wsList.find((w) => w?.id === joinedWsId)
    showToast?.(t('NLSInviteJoinedToast', { name: joinedWsName || 'workspace' }), 'info', {
      label: t('NLSInviteJoinedOpenAction'),
      run: () => {
        void requestWorkspaceSwitch({
          source: 'JOINED',
          toWorkspaceId: joinedWsId,
          landing: safeWorkspaceLanding(joinedWs, 'DASHBOARD'),
        })
      },
    })
    await load()
  }, [load, refreshWorkspaces, showToast, t, workspaces])

  const accept = async (invite: PendingInvite) => {
    setBusyId(invite.id)
    setError(null)
    setMessage(null)
    try {
      await claimPendingInvite(invite.id, claimingUser)
      await handleJoinedWorkspace(invite.workspaceId, invite.workspaceName)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not accept this invite.')
    } finally {
      setBusyId(null)
    }
  }

  const decline = async (invite: PendingInvite) => {
    if (invite.type === 'LINK') return
    setBusyId(invite.id)
    setError(null)
    setMessage(null)
    try {
      await declinePendingInvite(invite.id, claimingUser)
      setMessage('Invite rejected.')
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not reject this invite.')
    } finally {
      setBusyId(null)
    }
  }

  const handleJoinCodeClaimed = async (claimed: ClaimedJoinCodeInfo) => {
    setJoinWithCodeOpen(false)
    await handleJoinedWorkspace(claimed.workspaceId, claimed.workspaceName)
  }

  return (
    <section className="incomingInvitesPage" data-testid="incoming-invites-page">
      <header className="incomingInvitesPage__header">
        <div className="incomingInvitesPage__heading">
          <div>
            <p className="incomingInvitesPage__eyebrow">Incoming invites</p>
            <h2>Workspace requests for you</h2>
            <p>Invites sent to you.</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setJoinWithCodeOpen(true)}
          >
            {t('NLSJoinWithCodeAction')}
          </Button>
        </div>
        <RailChipTabs
          orientation="horizontal"
          ariaLabel="Invite filters"
          value={filter}
          onChange={(key) => setFilter(key as 'ALL' | 'PENDING' | 'CLAIMED' | 'DECLINED')}
          items={[
            { key: 'ALL', label: 'All' },
            { key: 'PENDING', label: 'Pending', count: counts.PENDING },
            { key: 'CLAIMED', label: 'Accepted', count: counts.CLAIMED },
            { key: 'DECLINED', label: 'Rejected', count: counts.DECLINED },
          ]}
        />
      </header>

      {error ? <p className="incomingInvitesPage__message incomingInvitesPage__message--error">{error}</p> : null}
      {message ? <p className="incomingInvitesPage__message">{message}</p> : null}

      <div className="incomingInvitesPage__list">
        {loading ? (
          <div className="incomingInvitesPage__card"><Loader size={140} label="Checking invites" /></div>
        ) : visibleInvites.length === 0 ? (
          <EmptyHubState appearance="card" preset="invites" icon={<MarkEmailUnreadRoundedIcon />} title="No incoming invites" description="Workspace admins can invite you from their Members panel." />
        ) : visibleInvites.map((invite) => (
          <article key={invite.id} className="incomingInvitesPage__card">
            <div className="incomingInvitesPage__inviteHeader">
              <div>
                <h3>{invite.workspaceName || 'Workspace'}</h3>
                <p>{inviteSubtitle(invite, t)}</p>
                {invite.invitedByName ? <p>From {invite.invitedByName}</p> : null}
              </div>
              <span className={`incomingInvitesPage__status incomingInvitesPage__status--${invite.status.toLowerCase()}`}>
                {STATUS_LABEL[invite.status]}
              </span>
            </div>
            {invite.status === 'PENDING' ? (
              <div className="incomingInvitesPage__actions">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busyId !== null}
                  onClick={() => void accept(invite)}
                >
                  {busyId === invite.id ? 'Accepting...' : 'Accept'}
                </Button>
                {invite.type !== 'LINK' ? (
                  <Button
                    variant="tertiary"
                    size="sm"
                    disabled={busyId !== null}
                    onClick={() => void decline(invite)}
                  >
                    {busyId === invite.id ? 'Working...' : 'Reject'}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </article>
        ))}
      </div>
      <JoinWithCodeDialog
        open={joinWithCodeOpen}
        onClose={() => setJoinWithCodeOpen(false)}
        onClaimed={handleJoinCodeClaimed}
      />
    </section>
  )
}
